// Mock-vs-real toggle for the submission-flow API.
//
// Default comes from REACT_APP_USE_MOCK_API (true only in .env.development).
// In non-production builds the URL can override it, and the override sticks
// for the tab so SPA navigation keeps the choice:
//   ?mock=1 / ?mock=0          force mock / real
//   ?mockScenario=expired      pick a mock scenario (see MockScenario)
//   ?mockLatency=2000          force response latency in ms
// Every query/storage override is hard-disabled in production builds — a
// shared production URL with ?mock=1 can never show fixture data.
//
// `?ctry=XX` is the one exception (see getForcedCountryCode).

const MOCK_OVERRIDE_KEY = "sfMockApiOverride";
const SCENARIO_KEY = "sfMockScenario";
const LATENCY_KEY = "sfMockLatencyMs";
const COUNTRY_KEY = "sfForcedCountryCode";

// Every value here must also appear in MOCK_SCENARIOS below — getMockScenario()
// validates against that array, so a scenario added to only one of the two
// silently degrades to "default".
export type MockScenario =
  | "default"
  | "expired"
  | "roleCast"
  | "notFound"
  | "slow"
  | "error500"
  | "rateLimit429"
  | "offline"
  | "geoFail"
  | "empty"
  // step 4 — membership states that skip or block the paywall
  | "alreadyPro"
  | "trialActive"
  | "usedTrial"
  | "notEligible"
  // step 4 — checkout outcomes
  | "cardDeclined"
  | "sessionExpired"
  // the payment's confirmation never arrives (the return page gives up waiting)
  | "webhookSlow"
  // step 4 — the send itself fails with a server error
  | "sendFails"
  // step 4 — Send refused because casting doesn't take the account's country
  // (countryNotEligible), after the plan if one is owed: the exits, or after a payment the
  // countryAfterPayment outcome (Signup/docs/16 §9)
  | "countryRefused"
  // the account already has a submission with casting for this breakdown
  | "alreadySubmitted"
  // an existing user (not a new signup): step 2 has no film center or age
  // statement, step 4 no job-alerts box
  | "existingUser"
  // the same, on a PRO plan: no phone step, nothing to pay
  | "existingPro"
  // an existing actor already logged in on the classic site: step 1 offers "Continue as"
  | "classicSession";

export const MOCK_SCENARIOS: MockScenario[] = [
  "default",
  "expired",
  "roleCast",
  "notFound",
  "slow",
  "error500",
  "rateLimit429",
  "offline",
  "geoFail",
  "empty",
  "alreadyPro",
  "trialActive",
  "usedTrial",
  "notEligible",
  "alreadySubmitted",
  "cardDeclined",
  "sessionExpired",
  "webhookSlow",
  "sendFails",
  "countryRefused",
  "existingUser",
  "existingPro",
  "classicSession",
];

const isProductionBuild = () => process.env.NODE_ENV === "production";

const hasWindow = () => typeof window !== "undefined";

// Reads a query param once and persists it to sessionStorage so the value
// survives SPA route changes (the query string only exists on the entry URL).
const readOverride = (param: string, storageKey: string): string | null => {
  if (!hasWindow() || isProductionBuild()) return null;
  try {
    const value = new URLSearchParams(window.location.search).get(param);
    if (value !== null && value !== "") {
      window.sessionStorage.setItem(storageKey, value);
      return value;
    }
    return window.sessionStorage.getItem(storageKey);
  } catch (e) {
    return null;
  }
};

export const isMockApi = (): boolean => {
  const override = readOverride("mock", MOCK_OVERRIDE_KEY);
  if (override === "1" || override === "true") return true;
  if (override === "0" || override === "false") return false;
  return process.env.REACT_APP_USE_MOCK_API === "true";
};

export const getMockScenario = (): MockScenario => {
  const value = readOverride("mockScenario", SCENARIO_KEY) as MockScenario;
  return MOCK_SCENARIOS.indexOf(value) > -1 ? value : "default";
};

export const getMockLatencyMs = (): number | null => {
  const value = readOverride("mockLatency", LATENCY_KEY);
  if (!value) return null;
  const ms = parseInt(value, 10);
  return isNaN(ms) || ms < 0 ? null : ms;
};

// Forces the country the flow believes the visitor is in: `?ctry=US`.
//
// Country-dependent copy ("Submitting is free for US talent" vs "…for your
// first 30 days") is otherwise untestable without a VPN, and geolocation is the
// one input a developer cannot fake locally.
//
// Unlike the mock switches above this is NOT gated on the build type, because
// `?ctry=` is already an app-wide convention that works in production —
// ActorFilmCenterStep, ActorStageNameStep and ActorCodeFilmCenterStep all read
// it to preselect a country. It only swaps which sentence renders; the trial
// entitlement itself is decided server-side at account creation, so forcing it
// cannot buy anyone a different plan.
//
// Persisted for the tab so it survives SPA navigation, like the switches above.
export const getForcedCountryCode = (): string | null => {
  if (!hasWindow()) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("ctry") || params.get("country");
    if (value) {
      const code = value.trim().toUpperCase();
      // ISO 3166-1 alpha-2 only, so a junk value cannot pin the copy to a
      // country that does not exist.
      if (/^[A-Z]{2}$/.test(code)) {
        window.sessionStorage.setItem(COUNTRY_KEY, code);
        return code;
      }
      return null;
    }
    return window.sessionStorage.getItem(COUNTRY_KEY);
  } catch (e) {
    return null;
  }
};
